/* MembershipGate — reusable page-level gate that enforces the Master
 * Gating Protocol on premium routes (Dashboard, Courses, RoyAI, etc.).
 *
 * Fetches `/api/royai/access-status` on mount. If the user isn't in the
 * allowed plans, renders a cinematic "Members Only" splash with a
 * spinning-lock animation and CTAs to /plans (upgrade) or /royai
 * (redeem VIP code) instead of the requested page.
 *
 * Props:
 *   allow   list of plans allowed to view the wrapped page
 *           default: ["master", "royai_elite", "passport_pro"]
 *   label   short label shown on the splash (e.g. "Dashboard")
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function MembershipGate({
  allow = ["master", "royai_elite", "passport_pro"],
  label = "This section",
  children,
}) {
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/royai/access-status");
        setAccess(data);
      } catch { /* treat as guest */ }
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-24 text-center text-neutral-500 font-mono-tech" data-testid="gate-loading">
          Verifying access…
        </div>
      </div>
    );
  }

  const plan = access?.plan || "guest";
  if (allow.includes(plan)) return children;

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="membership-gate">
        <div className="relative w-28 h-28 mx-auto mb-8" aria-hidden>
          <div className="absolute inset-0 rounded-full border border-amber-500/30 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]" />
          <div className="absolute inset-2 rounded-full border-2 border-amber-500/60" style={{ animation: "spin 8s linear infinite" }} />
          <div className="absolute inset-5 rounded-full border border-amber-500/40" style={{ animation: "spin 5s linear infinite reverse" }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <Lock size={40} className="text-amber-500" strokeWidth={1.4} style={{ filter: "drop-shadow(0 0 16px #D4AF37cc)" }} />
          </div>
        </div>

        <div className="text-[10px] font-mono-tech uppercase tracking-[0.22em] text-amber-500 mb-3">
          Members Only
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-4">
          {label} is <span className="text-amber-500">locked</span>.
        </h1>
        <p className="text-neutral-400 max-w-lg mx-auto mb-8 leading-relaxed">
          Unlock a Passport Pro or RoyAI Elite membership to access
          {" "}{label.toLowerCase()}. Free VIP trial holders get 60-minute
          access to MCS, SBD, SAL, and DDRR only.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/plans"
            data-testid="gate-upgrade-cta"
            className="inline-flex items-center gap-2 bg-amber-500 text-black hover:brightness-110 transition-all px-6 py-4 font-mono-tech text-xs uppercase tracking-[0.22em] font-semibold"
            style={{ boxShadow: "0 20px 60px -20px #D4AF37cc" }}
          >
            <Sparkles size={14} />
            See Membership Plans
            <ArrowRight size={14} />
          </Link>
          <Link
            to="/royai"
            data-testid="gate-vip-cta"
            className="inline-flex items-center gap-2 border border-amber-500 text-amber-500 hover:bg-amber-500/10 transition-all px-6 py-4 font-mono-tech text-xs uppercase tracking-[0.22em] font-semibold"
          >
            Claim VIP Trial Code
          </Link>
        </div>

        <div className="mt-8 text-[10px] font-mono-tech uppercase tracking-widest text-neutral-500">
          Master owner accounts bypass this gate automatically.
        </div>
      </main>
      <Footer />
    </div>
  );
}
